import { callStreamingEdgeFunction } from './edgeFunctionClient';
import type { ReviewProgress } from './edgeFunctionClient';
import type { Annotation } from './tenancyReviewRewrite';
import { truncateForAnnotation } from '../utils/documentTruncation';

export interface CommercialLeaseAnalysis {
  leaseType: string;
  termSummary: string;
  overallRisk: 'critical' | 'high' | 'medium' | 'low';
  complianceScore: number;
  issueCount: number;
  summary: string;
  keyTerms: Array<{
    term: string;
    detail: string;
    assessment: 'fair' | 'unfair' | 'onerous' | 'standard';
  }>;
  alerts: Array<{
    title: string;
    description: string;
    severity: 'critical' | 'high' | 'medium' | 'low';
    legalReference: string;
  }>;
  unusualClauses: string[];
  onerousTerms: Array<{
    clause: string;
    risk: string;
    recommendation: string;
  }>;
  hiddenObligations: string[];
  professionalAdviceAreas: string[];
  businessImpactAreas: Array<{
    area: string;
    impact: string;
    severity: 'critical' | 'high' | 'medium' | 'low';
  }>;
  negotiationLeverage: Array<{
    item: string;
    leverage: string;
    suggestedApproach: string;
  }>;
}

export interface CommercialLeaseDigest {
  analysis: CommercialLeaseAnalysis;
  documentB: string;
  annotations: Annotation[];
  negotiationSummary: string;
}

const DEFAULT_ANALYSIS: CommercialLeaseAnalysis = {
  leaseType: 'Unknown',
  termSummary: 'Unable to determine',
  overallRisk: 'high',
  complianceScore: 0,
  issueCount: 0,
  summary: '',
  keyTerms: [],
  alerts: [],
  unusualClauses: [],
  onerousTerms: [],
  hiddenObligations: [],
  professionalAdviceAreas: [],
  businessImpactAreas: [],
  negotiationLeverage: [],
};

const ANALYSIS_PROMPT = `You are a senior UK commercial property attorney performing a detailed initial read and analysis of two versions of a commercial lease. You have been retained by the tenant to review the deal.

You will receive DOCUMENT A (the original lease) and DOCUMENT B (the revised/proposed lease). Read both documents in full, including any letter of intent, term sheet, work letter for tenant improvements, and exhibits/schedules.

Assess both documents against ALL of the following legislation and codes:
- Landlord and Tenant Act 1954 (security of tenure, contracting out)
- Commercial Rent Arrears Recovery (CRAR) - Tribunals, Courts and Enforcement Act 2007
- Lease Code for Commercial Property 2007 (industry best practice)
- Landlord and Tenant (Covenants) Act 1995 (privity, AGAs)
- Regulatory Reform (Business Tenancies) (England and Wales) Order 2003
- Law of Property Act 1925 (formal requirements)
- Supply of Goods and Services Act 1982
- Unfair Contract Terms Act 1977

For BOTH documents, assess:
- Whether the lease is inside or outside the 1954 Act and implications
- Balance of obligations between landlord and tenant
- Rent review mechanism fairness (upward only vs open market)
- Break clause conditions and whether they are achievable
- Alienation restrictions (assignment, subletting)
- Repair and decoration obligations (FRI lease assessment)
- Service charge provisions and caps
- Insurance arrangements
- Guarantor/AGA provisions
- Hidden obligations or financial exposure
- Unusual or onerous clauses

Additionally, assess the BUSINESS IMPACT of key provisions on the tenant's operations:
- How clauses could affect signage, parking, equipment needs
- Whether use restrictions limit the tenant's growth plans or pivots
- Subletting flexibility for future downsizing or expansion
- Hidden costs that could affect cash flow

Identify areas where the tenant has NEGOTIATION LEVERAGE to push back.

Return ONLY valid JSON matching this structure:
{
  "leaseType": "e.g., Full Repairing and Insuring (FRI)",
  "termSummary": "brief term description",
  "overallRisk": "critical|high|medium|low",
  "complianceScore": 0-100,
  "issueCount": number,
  "summary": "brief overall assessment of both documents and how Document B compares to Document A",
  "keyTerms": [
    { "term": "Rent Review", "detail": "upward only, 5-yearly", "assessment": "fair|unfair|onerous|standard" }
  ],
  "alerts": [
    { "title": "title", "description": "detail", "severity": "critical|high|medium|low", "legalReference": "reference" }
  ],
  "unusualClauses": ["list of unusual provisions found in either document"],
  "onerousTerms": [
    { "clause": "clause description", "risk": "risk explanation", "recommendation": "what to negotiate" }
  ],
  "hiddenObligations": ["financial or practical obligations not immediately obvious"],
  "professionalAdviceAreas": ["areas where specialist advice is recommended"],
  "businessImpactAreas": [
    { "area": "e.g., Signage", "impact": "description of how this affects the tenant's business", "severity": "critical|high|medium|low" }
  ],
  "negotiationLeverage": [
    { "item": "e.g., Break clause", "leverage": "why tenant has leverage here", "suggestedApproach": "how to negotiate" }
  ]
}

IMPORTANT: Output ONLY the raw JSON object. Do not wrap it in markdown code fences. Do not include any text, commentary, or explanation before or after the JSON.`;

const COMPARISON_PROMPT = `You are a senior UK commercial property attorney performing a clause-by-clause comparison of two lease versions on behalf of the tenant.

You will receive DOCUMENT A (the original lease) and DOCUMENT B (the revised/proposed lease), along with an analysis summary.

Compare every material provision between the two documents. For each significant difference, produce a detailed annotation.

CRITICAL RULES FOR TEXT FIELDS:
- The "originalText" field MUST be an EXACT, VERBATIM substring copied character-for-character from DOCUMENT A. If the provision does not exist in Document A (newly added in B), set originalText to "".
- The "newText" field MUST be an EXACT, VERBATIM substring copied character-for-character from DOCUMENT B. If the provision was removed in Document B (exists only in A), set newText to "".
- Copy at least 30 characters of contiguous text that appears exactly in the respective document.
- NEVER paraphrase or summarize text in these fields.

For each difference, provide:
- The exact original text from Document A (verbatim)
- The exact new text from Document B (verbatim)
- A plain-English explanation of what changed, written for a business person (not a lawyer)
- Whether the change favours the landlord, the tenant, or is neutral
- The legal and commercial significance
- A severity rating (critical = significant legal/financial risk, high = important commercial concern, medium = best practice issue, low = drafting/clarity change)
- A recommendation: "accept" (change benefits tenant or is neutral), "reject" (change harms tenant, push back), or "negotiate" (seek middle ground) with specific counter-proposal language if applicable
- The relevant legal reference
- A category label (e.g., "Rent Review", "Break Clause", "Alienation", "Service Charge", "Repair Obligations", "Insurance", "1954 Act", "Guarantor/AGA", "Forfeiture", "Dispute Resolution", "Use Restrictions", "Alterations")

Return ONLY valid JSON as an array:
[
  {
    "id": "ann_1",
    "originalText": "exact verbatim text from Document A",
    "newText": "exact verbatim text from Document B",
    "reason": "plain-English explanation of what changed and why it matters commercially",
    "favoursParty": "landlord|tenant|neutral",
    "recommendation": "accept|reject|negotiate",
    "counterProposal": "specific suggested counter-language if recommendation is negotiate, otherwise empty string",
    "legalReference": "Act/Section/Lease Code reference",
    "severity": "critical|high|medium|low",
    "category": "category label"
  }
]

IMPORTANT: Output ONLY the raw JSON array. Do not wrap it in markdown code fences. Do not include any text before or after the JSON.`;

const NEGOTIATION_PROMPT = `You are a senior UK commercial property attorney preparing a negotiation strategy memo for your tenant client.

Based on the analysis and clause-by-clause comparison provided, write a comprehensive negotiation summary in plain English.

Structure your response EXACTLY as follows:

## Executive Summary
A 2-3 paragraph overview of the key differences between the two lease versions and the overall position.

## Must-Negotiate Items
A numbered list of the highest-priority items the tenant MUST push back on before signing. For each item:
- What the current position is
- Why it's problematic
- The specific counter-language or position to take

## Items to Accept
A brief list of changes that are acceptable or beneficial to the tenant.

## Business Impact Assessment
How specific clauses affect:
- Signage and branding
- Parking and access
- Equipment and fit-out
- Growth plans and flexibility
- Subletting and assignment options
- Hidden costs and cash flow

## Final Recommendation
One of: "SAFE TO SIGN as-is", "NEGOTIATE THESE ITEMS FIRST", or "SEEK FURTHER PROFESSIONAL ADVICE" with a brief explanation.

Write in clear, professional English suitable for a business owner who is not a lawyer.`;

function extractJsonByDepth(text: string): string | null {
  const firstBrace = text.indexOf('{');
  const firstBracket = text.indexOf('[');
  let startIdx = -1;
  let openChar = '{';
  let closeChar = '}';

  if (firstBrace === -1 && firstBracket === -1) return null;
  if (firstBrace === -1) { startIdx = firstBracket; openChar = '['; closeChar = ']'; }
  else if (firstBracket === -1) { startIdx = firstBrace; }
  else if (firstBracket < firstBrace) { startIdx = firstBracket; openChar = '['; closeChar = ']'; }
  else { startIdx = firstBrace; }

  let depth = 0;
  let inString = false;
  let escape = false;

  for (let i = startIdx; i < text.length; i++) {
    const ch = text[i];
    if (escape) { escape = false; continue; }
    if (ch === '\\' && inString) { escape = true; continue; }
    if (ch === '"') { inString = !inString; continue; }
    if (inString) continue;
    if (ch === openChar) depth++;
    else if (ch === closeChar) {
      depth--;
      if (depth === 0) return text.slice(startIdx, i + 1);
    }
  }
  return null;
}

function parseJSON<T>(text: string, fallback: T): T {
  try {
    const cleaned = text.replace(/```json\n?/g, '').replace(/```\n?/g, '').trim();
    return JSON.parse(cleaned);
  } catch {
    const extracted = extractJsonByDepth(text);
    if (extracted) {
      try {
        return JSON.parse(extracted);
      } catch {
        // fall through
      }
    }
    return fallback;
  }
}

export interface CommercialReviewOptions {
  onProgress?: (progress: ReviewProgress) => void;
  previousAnalysis?: CommercialLeaseAnalysis;
  signal?: AbortSignal;
}

const STAGE_RETRY_DELAY_MS = 3000;

async function retryStage<T>(
  fn: () => Promise<T>,
  signal?: AbortSignal,
): Promise<T> {
  try {
    return await fn();
  } catch (err) {
    if (signal?.aborted) throw err;
    const msg = err instanceof Error ? err.message.toLowerCase() : '';
    const isRetryable = msg.includes('stall') || msg.includes('timed out') ||
      msg.includes('504') || msg.includes('502') || msg.includes('network') ||
      msg.includes('overload') || msg.includes('rate_limit');
    if (!isRetryable) throw err;
    console.warn('Stage failed, retrying once after delay:', msg);
    await new Promise(r => setTimeout(r, STAGE_RETRY_DELAY_MS));
    if (signal?.aborted) throw err;
    return await fn();
  }
}

export async function performCommercialLeaseReview(
  documentTextA: string,
  documentTextB: string,
  options?: CommercialReviewOptions,
): Promise<CommercialLeaseDigest> {
  const { onProgress, previousAnalysis, signal } = options || {};
  let analysis: CommercialLeaseAnalysis;

  if (previousAnalysis) {
    analysis = previousAnalysis;
    onProgress?.({
      status: 'analysing',
      stage: 1,
      totalStages: 3,
      message: 'Using previous analysis results',
      percent: 33,
    });
  } else {
    onProgress?.({
      status: 'analysing',
      stage: 1,
      totalStages: 3,
      message: 'Reading and analysing both documents',
      percent: 2,
    });

    const analysisResult = await retryStage(async () => {
      let analysisChars = 0;
      return callStreamingEdgeFunction(
        {
          action: 'commercial-lease-review',
          systemPrompt: ANALYSIS_PROMPT,
          userPrompt: `DOCUMENT A (Original Lease):\n\n${documentTextA}\n\n---\n\nDOCUMENT B (Revised/Proposed Lease):\n\n${documentTextB}`,
          maxTokens: 4000,
          temperature: 0.1,
          model: 'claude-sonnet-4-6',
        },
        (chunk) => {
          analysisChars += chunk.length;
          const ratio = Math.min(analysisChars / 3000, 1);
          onProgress?.({
            status: 'analysing',
            stage: 1,
            totalStages: 3,
            message: 'Reading and analysing both documents',
            percent: Math.round(2 + ratio * 31),
            streamedChars: analysisChars,
          });
        },
        signal,
        {
          onRetry: () => onProgress?.({
            status: 'analysing', stage: 1, totalStages: 3,
            message: 'Retrying analysis...', percent: 5,
          }),
        },
      );
    }, signal);

    analysis = parseJSON<CommercialLeaseAnalysis>(analysisResult.content, DEFAULT_ANALYSIS);
  }

  onProgress?.({
    status: 'analysing',
    stage: 1,
    totalStages: 3,
    message: `Found ${analysis.issueCount} issues across both documents`,
    percent: 33,
  });

  onProgress?.({
    status: 'annotating',
    stage: 2,
    totalStages: 3,
    message: 'Comparing clauses and generating redline recommendations',
    percent: 34,
  });

  const { original: annOriginal, rewritten: annRewritten } = truncateForAnnotation(documentTextA, documentTextB);
  const annotationsResult = await retryStage(async () => {
    let annotationChars = 0;
    return callStreamingEdgeFunction(
      {
        action: 'commercial-lease-review',
        systemPrompt: COMPARISON_PROMPT,
        userPrompt: `DOCUMENT A (Original Lease):\n\n${annOriginal}\n\n---\n\nDOCUMENT B (Revised/Proposed Lease):\n\n${annRewritten}\n\n---\n\nANALYSIS SUMMARY:\n\n${JSON.stringify(analysis)}`,
        maxTokens: 8000,
        temperature: 0.1,
        model: 'claude-sonnet-4-6',
      },
      (chunk) => {
        annotationChars += chunk.length;
        const ratio = Math.min(annotationChars / 6000, 1);
        onProgress?.({
          status: 'annotating',
          stage: 2,
          totalStages: 3,
          message: 'Comparing clauses and generating redline recommendations',
          percent: Math.round(34 + ratio * 32),
          streamedChars: annotationChars,
        });
      },
      signal,
      {
        onRetry: () => onProgress?.({
          status: 'annotating', stage: 2, totalStages: 3,
          message: 'Retrying clause comparison...', percent: 38,
        }),
      },
    );
  }, signal);

  const annotations = parseJSON<Annotation[]>(annotationsResult.content, []);

  onProgress?.({
    status: 'annotating',
    stage: 2,
    totalStages: 3,
    message: `Identified ${annotations.length} material differences`,
    percent: 66,
  });

  onProgress?.({
    status: 'finalising',
    stage: 3,
    totalStages: 3,
    message: 'Preparing negotiation strategy',
    percent: 67,
  });

  const negotiationResult = await retryStage(async () => {
    let negChars = 0;
    return callStreamingEdgeFunction(
      {
        action: 'commercial-lease-review',
        systemPrompt: NEGOTIATION_PROMPT,
        userPrompt: `ANALYSIS:\n\n${JSON.stringify(analysis)}\n\n---\n\nCLAUSE-BY-CLAUSE COMPARISON:\n\n${JSON.stringify(annotations)}`,
        maxTokens: 4000,
        temperature: 0.2,
        model: 'claude-sonnet-4-6',
      },
      (chunk) => {
        negChars += chunk.length;
        const ratio = Math.min(negChars / 3000, 1);
        onProgress?.({
          status: 'finalising',
          stage: 3,
          totalStages: 3,
          message: 'Preparing negotiation strategy',
          percent: Math.round(67 + ratio * 30),
          streamedChars: negChars,
        });
      },
      signal,
      {
        onRetry: () => onProgress?.({
          status: 'finalising', stage: 3, totalStages: 3,
          message: 'Retrying negotiation summary...', percent: 70,
        }),
      },
    );
  }, signal);

  const negotiationSummary = negotiationResult.content;

  onProgress?.({
    status: 'complete',
    stage: 3,
    totalStages: 3,
    message: 'Review complete',
    percent: 100,
  });

  return {
    analysis,
    documentB: documentTextB,
    annotations,
    negotiationSummary,
  };
}
